/**
 * VENDORED from tc-console/src/home/Sparkline.tsx — DO NOT EDIT.
 *
 * Refresh with scripts/sync-home.sh; edit the canonical file instead. See the
 * script header for what is copied verbatim and what is an adaptation.
 */
/**
 * A sparkline: the shape behind a number, inside its own card.
 *
 * Deliberately the smallest possible chart. No axes, no grid, no legend (one
 * series — the card's label names it), no numbers on points. It answers "which
 * way, and how bumpy", and the headline number above it answers "how many". A
 * micro-chart that tries to also be readable in absolute terms ends up doing
 * neither job.
 *
 * Colour is the brand accent, or Home's own accent when a content repo sets one.
 * Validated against both surfaces: brand red passes the lightness band, chroma
 * floor and 3:1 contrast on the light and dark chart surfaces alike, so one hue
 * serves both themes rather than each mode inventing its own.
 *
 * Accessibility: the number is the accessible content — the line is decorative
 * reinforcement, so it is aria-hidden and the card's text carries the meaning,
 * with a native <title> for a hover read-out of the range.
 */

import { useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'

const W = 96
const H = 28
/** Room for the stroke and the end dot, so neither is clipped at the edges. */
const PAD = 3

export default function Sparkline({
  values,
  accent,
  label,
}: {
  values: number[]
  accent?: string
  /** Used in the hover title: "Enrollments · 0–48 per day". */
  label?: string
}) {
  const theme = useTheme()
  if (values.length < 2) return null
  const color = accent ?? theme.palette.primary.main
  const max = Math.max(...values)
  const min = Math.min(...values)
  // A flat series is drawn flat down the middle rather than divided by zero.
  const span = max - min || 1
  const x = (i: number) => PAD + (i * (W - PAD * 2)) / (values.length - 1)
  const y = (v: number) => H - PAD - ((v - min) / span) * (H - PAD * 2)

  const line = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
  const area = `${line} L${x(values.length - 1).toFixed(1)},${H} L${x(0).toFixed(1)},${H} Z`
  const lastX = x(values.length - 1)
  const lastY = y(values[values.length - 1])

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block' }}
    >
      <title>{`${label ? `${label} · ` : ''}${min}–${max} per day`}</title>
      <path d={area} fill={alpha(color, 0.14)} stroke="none" />
      <path d={line} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      {/* The one direct mark: where the series ends, which is where the eye goes. */}
      <circle cx={lastX} cy={lastY} r={2.5} fill={color} stroke={theme.palette.background.paper} strokeWidth={1.5} />
    </svg>
  )
}
